import React, { useState } from 'react';

import styled from 'styled-components';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useRouter } from 'next/router';
import { MoreHorizontal, Share2 } from 'react-feather';
import useTranslation from 'next-translate/useTranslation';
import { style, User, useIsLoggedIn } from '../../api';
import { Role } from '../../api/lib/api/user';

import { Menu, MenuItem } from '../common/Menu';
import { Location } from '../common/Icons';
import Loader from '../common/Loader';
import { GradientButton, WhiteButton } from '../common/Buttons';
import { LoggedIn } from '../../utils/Logged';
import ModalPortal from '../common/Modal';
import UserReport from '../Modals/UserReport';
import SharingModal from '../Modals/SharingModal';
import BlockUserModal from '../Modals/BlockUser';
import ProfileFollowersLabel from '../common/ProfileFollowersLabel';
import {
  useIsDesktop,
  Desktop,
  noDesktop,
  desktop,
} from '../../style/constants';
import UserImageStatus from '../common/UserImageStatus';
import { TextBold22 } from '../common/Text';
import { RequestFeedbackButton, FollowButton } from '../common/Buttons/V2';
import StatsButton from '../common/Buttons/V2/StatsButton';
import { Link } from '../common/Miscellanea';
import EditProfileButton from '../common/Buttons/V2/EditProfileButton';
import useLoginWithRedirect from '../../hooks/useLoginWithRedirect';
import ProLogo from '../common/ProLogo';
import { Overlay } from '../common/Uploadable';

export interface StateProps {
  isOnline: boolean;
  loggedIn: boolean;
  myRole?: Role;
}

export const coverImageHeight = 170;

export interface DispatchProps {}

export interface OwnProps {
  profileId?: number;
  user?: User;
  isReady: boolean;
  notCurrent: boolean;
  showFeedbackModal: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const ProfileHeader = ({
  user,
  isReady,
  notCurrent,
  isOnline,
  loggedIn,
  myRole,
  showFeedbackModal,
}: Props) => {
  const { t } = useTranslation('profile');
  const [action, setAction] = useState<'block' | 'share' | 'report'>();

  const isDesktop = useIsDesktop();

  const redirect = useLoginWithRedirect(false);

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'my-profile-menu',
    disableAutoFocus: true,
  });

  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();

  if (!isReady || !user) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const {
    id,
    role,
    profile_cover: cover,
    profile_photo: photo,
    display_name: name,
    member,
  } = user;

  const onShare = async () => {
    popupState.close();

    setAction('share');
  };

  const onReport = async () => {
    popupState.close();

    await redirect();

    setAction('report');
  };

  const onBlock = async () => {
    popupState.close();

    await redirect();

    setAction('block');
  };

  const onMessage = () => {
    popupState.close();

    if (!isLoggedIn) {
      redirect();
      return;
    }

    router.push({
      pathname: '/chat',
      query: {
        messageTo: user.id,
      },
    });
  };

  return (
    <Wrapper>
      <CoverImage src={cover} />
      <Desktop>
        <BorderLeft />
      </Desktop>
      <ContentWrapper isCurrent={!notCurrent}>
        <UserImageStatus
          src={photo}
          isOnline={isOnline}
          size={isDesktop ? 100 : 70}
          displayStatus={loggedIn}
        />
        <UserInfoContainer>
          <TextBold22>{name}</TextBold22>
          {user?.role === 'label' && (
            <LabelInfoContainer>
              {/* <p style={{ margin: 0, marginRight: 22 }}>
                {`${formatLabelCreated(
                  (user?.created_at as unknown) as number,
                )} old Label`}
              </p> */}
              {user?.country?.name && (
                <>
                  <Location
                    size={14}
                    color={style.grey_dark_new}
                    style={{ marginRight: 4 }}
                  />
                  <p style={{ margin: 0 }}>{user?.country?.name}</p>
                </>
              )}
            </LabelInfoContainer>
          )}
        </UserInfoContainer>
        {member && <ProLogo />}
        <ButtonsWrapper>
          {notCurrent ? (
            <>
              {role !== 'label' && (
                <>
                  <MoreHorizontal
                    size={28}
                    style={{ marginRight: isDesktop ? 18 : 0 }}
                    {...bindTrigger(popupState)}
                  />
                  <Menu {...bindMenu(popupState)} className="br-10">
                    <MenuItem onClick={onMessage}>
                      <span>{t('profile:message')}</span>
                    </MenuItem>
                    <MenuItem onClick={onShare}>
                      <span>{t('profile:share')}</span>
                    </MenuItem>
                    <MenuItem onClick={onBlock}>
                      <span>
                        {user.blocked
                          ? t('profile:unblock')
                          : t('profile:block')}
                      </span>
                    </MenuItem>
                    <MenuItem onClick={onReport}>
                      <span>{t('profile:report')}</span>
                    </MenuItem>
                  </Menu>
                </>
              )}
              {user.role === 'label' && (
                <Desktop>
                  <ProfileFollowersLabel followers={user?.total_followers} />
                </Desktop>
              )}
              <LoggedIn>
                {notCurrent && role !== 'label' && myRole === 'producer' && (
                  <Desktop>
                    <RequestFeedbackButton onClick={showFeedbackModal} />
                  </Desktop>
                )}
              </LoggedIn>
              <Desktop>
                <FollowButton userId={user.id} />
              </Desktop>
            </>
          ) : (
            <>
              <Share2
                size={18}
                style={{
                  marginRight: isDesktop ? 18 : 10,
                  marginTop: isDesktop ? 9 : 15,
                }}
                {...bindTrigger(popupState)}
                onClick={onShare}
              />
              <Desktop>
                <Link legacyBehavior href="/profile/edit">
                  <EditProfileButton />
                </Link>
                <Link legacyBehavior href="/statistics">
                  <StatsButton />
                </Link>
              </Desktop>
            </>
          )}
        </ButtonsWrapper>
      </ContentWrapper>

      <ModalPortal
        isOpened={action === 'report'}
        toggle={() => setAction(undefined)}
      >
        <UserReport id={id} close={() => setAction(undefined)} />
      </ModalPortal>

      <BlockUserModal
        userId={user.id}
        open={action === 'block'}
        toggle={() => setAction(undefined)}
      />
      <SharingModal
        type="profile"
        id={user.id}
        showCopyButton
        show={action === 'share'}
        close={() => setAction(undefined)}
      />
    </Wrapper>
  );
};

export default ProfileHeader;

export const Wrapper = styled.div`
  .dropdown-select-zone {
    display: none;
  }
  .dropdown-select-menu {
    border-radius: 0px;
    height: 40px;
    width: 40px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    border-radius: 10px;
  }
  .dropdown-select-multi-value-wrapper {
    padding: 0px;
    display: flex;
    justify-content: center;
  }
  .dropdown-select-value {
    padding: 0px;
  }
  .dropdown-select-menu-outer {
    width: 150px;
    @media ${noDesktop} {
      right: 0px;
    }
  }

  position: relative;

  ${GradientButton}, ${WhiteButton} {
    text-transform: capitalize;
    font-weight: 500;
  }

  width: 100%;

  @media ${noDesktop} {
    height: 225px;
    margin-top: -30px;
  }

  @media ${desktop} {
    height: 245px;
  }
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: auto;
  min-height: 345px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CoverImage = styled.div<{ src: string }>`
  position: absolute;
  top: 0px;
  left: 0px;
  height: ${coverImageHeight}px;
  width: 100%;
  background-image: url('${(p) => p.src}');
  background-size: cover;
  background-position: 50% 50%;
`;

export const ContentWrapper = styled.div<{ isCurrent?: boolean }>`
  position: absolute;
  bottom: 0px;
  width: 100%;
  display: flex;
  align-items: center;

  ${Overlay} {
    border-radius: 50%;
  }

  ${ProLogo} {
    margin: 0px;
    top: 5px;
    left: 5px;
    position: relative;
  }

  @media ${desktop} {
    padding-left: 36px;
    padding-right: ${(p) => (p.isCurrent ? 0 : 6)}px;
    ${ProLogo} {
      margin-top: 26px;
    }
  }

  @media ${noDesktop} {
    padding-left: 18px;
    padding-right: ${(p) => (p.isCurrent ? 0 : 24)}px;

    ${ProLogo} {
      margin-top: 12px;
    }
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  margin-left: auto;

  @media ${desktop} {
    padding-top: 30px;
    ${StatsButton} {
      margin-right: 8px;
    }
  }

  @media ${noDesktop} {
    padding-top: 10px;
  }

  ${RequestFeedbackButton} {
    margin-right: 10px;
  }

  ${EditProfileButton} {
    margin-right: 8px;
  }
`;

const BorderLeft = styled.div`
  width: 1px;
  height: 90px;
  left: 0px;
  bottom: -30px;
  background-color: ${style.grey_light_1_new};
  position: absolute;
`;

const LabelInfoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  margin-top: 8px;
`;

const UserInfoContainer = styled.div`
  position: relative;
  top: 16px;
  margin-left: 16px;
`;
